import { ReactNode } from 'react';

import Text from '@cartwhl/ui/base/Text';

import ContactButton from './ContactButton';
import MobilePlanCards from './MobilePlanCards';
import { PlanRow, planRows } from './tableSectionData';

export default function TableSection() {
  return (
    <section className="pb-(--vertical-spacing)">
      <div className="container">
        {/* Desktop */}
        <div className="hidden lg:block">
          <div className="overflow-x-auto">
            <div>
              <TableHeader />
              <div className="mt-14 flex flex-col gap-8">
                {planRows.map((row) => (
                  <TableRow key={row.label} row={row} />
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Mobile */}
        <div className="flex flex-col gap-16 lg:hidden">
          <MobilePlanCards />
          <MobileCompareTable />
        </div>
      </div>
    </section>
  );
}

function TableHeader() {
  return (
    <div className="grid grid-cols-[1fr_200px_200px_200px] items-center gap-10 xl:grid-cols-[1fr_repeat(3,1fr)]">
      <Text as="div" variant="web/display/lg" xl:variant="web/display/xl">
        Plans
      </Text>
      <PlanHeader label="Standard" dotClassName="bg-gray-45" />
      <PlanHeader label="Pro" dotClassName="bg-blue-50" />
      <PlanHeader label="Scale" dotClassName="bg-green-50" />
    </div>
  );
}

function PlanHeader({
  label,
  dotClassName,
}: {
  label: string;
  dotClassName: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className={`h-6 w-6 flex-none rounded-full ${dotClassName}`} />
      <Text as="div" variant="web/display/lg" xl:variant="web/display/xl">
        {label}
      </Text>
    </div>
  );
}

function TableRow({ row }: { row: PlanRow }) {
  return (
    <div className="border-primary grid grid-cols-[1fr_200px_200px_200px] items-start gap-10 border-t py-8 xl:grid-cols-[1fr_repeat(3,1fr)]">
      <Text
        as="div"
        variant="web/body/medium/md"
        lg:variant="web/body/medium/24"
        className="text-tertiary"
      >
        {row.label}
      </Text>
      <TableCell content={row.standard} />
      <TableCell content={row.pro} />
      <TableCell content={row.enterprise} />
    </div>
  );
}

function TableCell({ content }: { content: ReactNode | string[] }) {
  if (Array.isArray(content)) {
    return (
      <div className="flex flex-col gap-6">
        {content.map((item) => (
          <Text
            key={item}
            as="div"
            variant="web/body/medium/md"
            lg:variant="web/body/medium/lg"
            xl:variant="web/body/medium/24"
          >
            {item}
          </Text>
        ))}
      </div>
    );
  }

  if (content === 'contact') {
    return <ContactButton />;
  }

  return (
    <Text
      as="div"
      variant="web/body/medium/md"
      lg:variant="web/body/medium/lg"
      xl:variant="web/body/medium/24"
    >
      {content}
    </Text>
  );
}

// ---------------------------------------------------------------------------
// Mobile components (shown below lg breakpoint)
// ---------------------------------------------------------------------------

function MobileCompareTable() {
  const compareRows = planRows.filter(
    (r) => r.label !== "Who it's for" && r.label !== 'API Access',
  );

  return (
    <div>
      <Text as="div" variant="web/display/2xl" className="mb-8">
        Compare
      </Text>

      {/* Column headers */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        <Text as="div" variant="web/body/medium/md">
          Standard
        </Text>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-blue-50" />
          <Text as="div" variant="web/body/medium/md">
            Pro
          </Text>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-green-50" />
          <Text as="div" variant="web/body/medium/md">
            Enterprise
          </Text>
        </div>
      </div>

      {/* Rows */}
      <div className="flex flex-col">
        {compareRows.map((row) => (
          <MobileCompareRow key={row.label} row={row} />
        ))}
      </div>
    </div>
  );
}

function MobileCompareRow({ row }: { row: PlanRow }) {
  const short = row.short;

  return (
    <div className="border-primary border-t py-6">
      {short?.label ? (
        <>
          <Text
            as="div"
            variant="web/body/medium/md"
            className="text-tertiary mb-3 sm:hidden"
          >
            {short.label}
          </Text>
          <Text
            as="div"
            variant="web/body/medium/md"
            className="text-tertiary mb-3 hidden sm:block"
          >
            {row.label}
          </Text>
        </>
      ) : (
        <Text
          as="div"
          variant="web/body/medium/md"
          className="text-tertiary mb-3"
        >
          {row.label}
        </Text>
      )}
      <div className="grid grid-cols-3 gap-4">
        <MobileCompareCell
          content={row.standard}
          shortContent={short?.standard}
        />
        <MobileCompareCell content={row.pro} shortContent={short?.pro} />
        <MobileCompareCell
          content={row.enterprise}
          shortContent={short?.enterprise}
        />
      </div>
    </div>
  );
}

function MobileCompareCell({
  content,
  shortContent,
}: {
  content: ReactNode | string[];
  shortContent?: ReactNode | string[];
}) {
  if (!shortContent) {
    return <CellContent content={content} />;
  }

  return (
    <>
      <div className="sm:hidden">
        <CellContent content={shortContent} />
      </div>
      <div className="hidden sm:block">
        <CellContent content={content} />
      </div>
    </>
  );
}

function CellContent({ content }: { content: ReactNode | string[] }) {
  if (Array.isArray(content)) {
    return (
      <div className="flex flex-col gap-5">
        {content.map((item) => (
          <Text key={item} as="div" variant="web/body/medium/md">
            {item}
          </Text>
        ))}
      </div>
    );
  }

  return (
    <Text as="div" variant="web/body/medium/md">
      {content}
    </Text>
  );
}
